import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SelectedTab, selectSelectedTab } from "../../app/slices/GlobalSlice";
import {
  selectSelectedElementType,
  selectSelectedTake3D,
  setSelectedTake,
  updateSelectedTake,
  updateSelectedTakeProperty,
} from "../../app/slices/TrainingSlice";
import PiSwitch from "../../components/PiSwitch";
import { InteractionProperty } from "../../models/InteractionProperty";
import Take3D from "../../models/Take3D";
import { ITransition } from "../../models/interactions/Transition";
import { normalizeTransition } from "../../models/interactions/TransitionDefaults";
import PiPopupWindowPreview from "../previews/components/PiPopupWindowPreview";
import TransitionEditor from "./TransitionEditor";

export default function ViewportTransitionWindow() {
  const dispatch = useDispatch();
  const selectedTab = useSelector(selectSelectedTab);
  const selectedElementType = useSelector(selectSelectedElementType);
  const selectedTake = useSelector(selectSelectedTake3D);

  const isVisible = useMemo(() => {
    return (
      selectedTab === SelectedTab.Timeline &&
      selectedElementType === "transition" &&
      selectedTake !== undefined
    );
  }, [selectedElementType, selectedTab, selectedTake]);

  if (!isVisible || !selectedTake) return null;

  const transition = normalizeTransition(selectedTake.transition);
  const transitionProp = selectedTake.properties?.find(
    (property) => property.id === "transition"
  );
  const isEnabled = Boolean(transitionProp?.value);

  const updateTransition = (newTransition: ITransition) => {
    dispatch(
      updateSelectedTake({
        ...selectedTake,
        transition: newTransition,
      } as Take3D)
    );
  };

  const onToggleTransition = (enabled: boolean) => {
    if (!transitionProp) return;

    dispatch(
      updateSelectedTakeProperty({
        ...transitionProp,
        value: enabled,
      } as InteractionProperty)
    );
  };

  const title = (
    <div className="viewport3d-actor-window__title-wrap">
      <div className="viewport3d-actor-window__icon">
        <ChangeCircleOutlinedIcon fontSize="inherit" color="inherit" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="viewport3d-actor-window__eyebrow">
          Transition Settings
        </div>
        <div className="viewport3d-actor-window__title" title={transition.name}>
          {transition.name}
        </div>
      </div>
      {transitionProp && (
        <div className="viewport3d-actor-window__title-switch">
          <PiSwitch
            label={transitionProp.name}
            labelLocId={transitionProp.locId}
            tooltipLocId={transitionProp.tooltipLocId}
            checked={isEnabled}
            onChange={onToggleTransition}
          />
        </div>
      )}
      <button
        type="button"
        aria-label="Close transition settings"
        className="viewport3d-actor-window__close"
        onClick={() => dispatch(setSelectedTake(selectedTake.id))}
      >
        <CloseOutlinedIcon fontSize="inherit" color="inherit" />
      </button>
    </div>
  );

  return (
    <aside
      aria-label="viewport-transition-window"
      className="viewport3d-transition-window"
    >
      <PiPopupWindowPreview
        title={title}
        size="medium"
        body={
          <div className="viewport3d-transition-window__body">
            <TransitionEditor
              transition={transition}
              disabled={!isEnabled}
              onChange={updateTransition}
            />
          </div>
        }
      />
    </aside>
  );
}
